/**
 * adminLifecycleMutations — the take-down and archive lifecycle mutations
 * (024-cms US6 / FR-024, US16). Part of the `useAdminContent` decomposition.
 *
 * ONE MUTATION PER OPERATION, ADDRESSED TO THE UNIT ROOT. Each hook here
 * calls the single wire procedure for its operation and passes the root's
 * id; the SERVER resolves what the publication unit contains. Callers do
 * not know, and cannot express, whether that unit has one member or five.
 * The in a unit single-item hooks these replaced, and the per-surface topology
 * derivations that chose between them, were deleted with the wire collapse.
 *
 * `restoreFromRetire` is the exception and stays row-addressed: it has no
 * unit counterpart at the port.
 */

import { type UseMutationResult, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import type { WebTrpcClient } from '../../../trpc/client.js';
import {
  ADMIN_CONTENT_LIST_QUERY_KEY,
  ADMIN_FIXED_ENTRIES_QUERY_KEY,
  adminContentGetQueryKey,
} from './adminContentQueries.js';
import { useAdminTrpc } from './adminTrpcContext.js';
import { useCommittedToast } from './lifecycleToast.js';

export type RetireContentInput = Parameters<
  WebTrpcClient['admin']['content']['retireUnit']['mutate']
>[0];

export type RetireContentResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['retireUnit']['mutate']>
>;

export type UnpublishContentInput = Parameters<
  WebTrpcClient['admin']['content']['unpublishUnit']['mutate']
>[0];

export type UnpublishContentResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['unpublishUnit']['mutate']>
>;

export type DiscardStagedEditInput = Parameters<
  WebTrpcClient['admin']['content']['discardStagedEdit']['mutate']
>[0];

export type DiscardStagedEditResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['discardStagedEdit']['mutate']>
>;

export type RestoreFromRetireInput = Parameters<
  WebTrpcClient['admin']['content']['restoreFromRetire']['mutate']
>[0];

export type RestoreFromRetireResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['restoreFromRetire']['mutate']>
>;

/**
 * Shared success-side cache sweep for the archive-lifecycle mutations:
 * both transitions move a row between the catalog's default list
 * (`includeRetired: false`) and the Arkisto drawer's list
 * (`includeRetired: true`), so the whole list-key family is invalidated
 * via the exported prefix, plus the item's own `get` entry (an open
 * editor form must re-derive its state from the new lifecycle state).
 */
function invalidateAfterLifecycleChange(
  queryClient: ReturnType<typeof useQueryClient>,
  itemId: string,
): void {
  void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ADMIN_FIXED_ENTRIES_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: adminContentGetQueryKey(itemId) });
}

/**
 * What the archive's post-error reconcile established. `'idle'` until a call
 * rejects; `'pending'` while the probe is on the wire.
 */
export type RetireReconcileState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'pending' }
  /** The call rejected but the unit root IS retired: the archive landed. */
  | { readonly kind: 'retiredDespiteError' }
  /** The unit root is not retired, or the probe itself failed. */
  | { readonly kind: 'genuineError'; readonly originalError: Error };

export interface RetireWithReconcileArgs {
  /**
   * Ran when the archive HAS landed — on a plain success, and on a rejected
   * call whose reconcile probe reports the root retired. A reconciled
   * archive is a success and gets no separate treatment.
   */
  readonly onRetired: () => void;
}

export interface RetireWithReconcileResult {
  /** Standard React Query mutation triple for the one archive procedure. */
  readonly mutation: UseMutationResult<RetireContentResult, Error, RetireContentInput>;
  /** Post-error reconcile state. `'idle'` until the first error fires. */
  readonly reconcile: RetireReconcileState;
  /** Clear the mutation AND the reconcile state — a fresh attempt starts clean. */
  readonly reset: () => void;
}

/**
 * Archive the publication unit rooted at `unitRootId` (024-cms US6 / FR-024) —
 * the `retired` transition surfaced as an explicit management action. Every
 * component retires in one transaction, each writing its own
 * `cms.content.retire` audit event; pending publish windows are DROPPED
 * (owner-ruled: there is no coherent archived-but-republishing-Friday, so
 * the archive action IS the decision).
 *
 * Reconcile-on-error, the archive's post-commit posture (the D1 class,
 * mirroring `usePublishWithReconcile`): the SERIALIZABLE transaction commits
 * BEFORE the router's output validation, so a retire can change server
 * state and still surface as an error. On `onError` the hook probes
 * `admin.content.get(unitRootId)`: the root reads `retired` → the archive
 * landed and `onRetired` runs exactly as it does on success; any other
 * state, or a failed probe → `genuineError`, and the caller keeps its
 * failure UI with retry (a retry against a landed archive would hit
 * `already-retired`, which is why the probe decides first). Switching the
 * caller to an unconditional `onSettled` was rejected: it would dismiss on
 * GENUINE failures.
 *
 * Invalidation runs in `onSettled` (success AND error), after the probe. The
 * sweep is a PREFIX sweep because the operation moves rows whose ids this
 * layer does not enumerate — the unit's membership is the server's.
 */
export function useRetireWithReconcile({
  onRetired,
}: RetireWithReconcileArgs): RetireWithReconcileResult {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const [reconcile, setReconcile] = useState<RetireReconcileState>({ kind: 'idle' });

  const mutation = useMutation<RetireContentResult, Error, RetireContentInput>({
    mutationFn: (input) => client.admin.content.retireUnit.mutate(input),
    onMutate: () => {
      setReconcile({ kind: 'idle' });
    },
    onSuccess: () => {
      onRetired();
    },
    onError: async (originalError, input) => {
      setReconcile({ kind: 'pending' });
      try {
        const detail = await client.admin.content.get.query({ id: input.unitRootId });
        if (detail.state === 'retired') {
          setReconcile({ kind: 'retiredDespiteError' });
          onRetired();
        } else {
          setReconcile({ kind: 'genuineError', originalError });
        }
      } catch {
        // The probe itself failed: "landed" and "rolled back" cannot be told
        // apart, so surface the original error and let the author retry.
        setReconcile({ kind: 'genuineError', originalError });
      }
    },
    onSettled: () => {
      invalidateAfterUnitChange(queryClient);
    },
  });

  const reset = (): void => {
    mutation.reset();
    setReconcile({ kind: 'idle' });
  };

  return { mutation, reconcile, reset };
}

/**
 * Take the publication unit rooted at `unitRootId` down (024-cms US16-A /
 * US16-F: `published` -> `draft` for every component, in one transaction).
 * `publishedAt` survives untouched (`PgContentItemStore.updateStateDraft`),
 * so a row taken down here keeps its `archive` affordance.
 *
 * PENDING WINDOWS GO WITH A MANUAL TAKE-DOWN (owner-ruled 2026-08-26, D-196):
 * the author cancelling the publication cancels its pending triggers, and
 * the unit door does that inside this one call. The composite CONFIRM lives
 * at the call site (`useTakedownConfirm`), which is why this hook takes no
 * position on windows and calls nothing but the take-down.
 *
 * Invalidation runs in `onSettled` (success AND error) — the D1 class: the
 * SERIALIZABLE transaction commits BEFORE the router's post-commit detail
 * fetch + output validation, so components can already be `draft` while the
 * call surfaces as an error. Sweeping only `onSuccess` would leave stale
 * rows with their old published controls plus a false "failed" signal.
 */
export function useUnpublishContent(): UseMutationResult<
  UnpublishContentResult,
  Error,
  UnpublishContentInput
> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<UnpublishContentResult, Error, UnpublishContentInput>({
    mutationFn: (input) => client.admin.content.unpublishUnit.mutate(input),
    // The toast is `onSuccess`, NOT `onSettled` beside the sweep. The sweep
    // runs either way because a take-down can land and still surface as an
    // error; the toast must not, because it would then claim a commit on a
    // path where we do not know one happened.
    onSuccess: () => {
      announce('cms.unpublish.success');
    },
    onSettled: () => {
      invalidateAfterUnitChange(queryClient);
    },
  });
}

/**
 * Mutate `admin.content.restoreFromRetire` — restore (024-cms US6 /
 * FR-024): a `retired` item returns to `draft` pending a fresh publish,
 * with the draft seeded server-side from the last published revision.
 * Audited `cms.content.restore`.
 */
export function useRestoreFromRetire(): UseMutationResult<
  RestoreFromRetireResult,
  Error,
  RestoreFromRetireInput
> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<RestoreFromRetireResult, Error, RestoreFromRetireInput>({
    mutationFn: (input) => client.admin.content.restoreFromRetire.mutate(input),
    onSuccess: (_result, input) => {
      invalidateAfterLifecycleChange(queryClient, input.id);
      // ONE carrier for this sentence. The drawer used to announce it into its
      // own `aria-live` line; both that and this toast are live regions, so
      // keeping both would read it out twice.
      announce('cms.archive.restored');
    },
  });
}

/**
 * Mutate `admin.content.discardStagedEdit` — drop a staged CONTENT EDIT and
 * leave the live content exactly as it is (043 US4, FR-009). Audited
 * `cms.content.discard_staged_edit`.
 *
 * THE SUBJECT IS THE PENDING EDIT, never the unit. The unit stays `published`,
 * `published_at` is unchanged and the live snapshot stays byte-identical; only
 * the content-bearing draft row goes. That is what separates this from the
 * take-down beside it (which changes the unit's visibility) and from archive
 * (which retires the unit).
 *
 * WHY THE LIST SWEEP IS THE POINT OF THIS HOOK, not bookkeeping: the row's
 * "unpublished content changes" marker derives from the draft KIND rather than
 * from a stored flag, so the row keeps reporting a change it no longer has
 * until the list is re-read. Without the sweep the author discards an edit and
 * watches the badge sit there, which reads as a failed operation.
 *
 * Invalidation runs in `onSettled` for the same reason the take-down's does:
 * the transaction commits before the router's post-commit detail fetch and
 * output validation, so the draft row can already be gone while the call
 * surfaces as an error. Sweeping only on success would leave a stale marker
 * beside a false failure.
 *
 * The toast is `onSuccess` ONLY, because it claims a commit — a path where we
 * do not know one happened must not announce one.
 */
export function useDiscardStagedEdit(): UseMutationResult<
  DiscardStagedEditResult,
  Error,
  DiscardStagedEditInput
> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<DiscardStagedEditResult, Error, DiscardStagedEditInput>({
    mutationFn: (input) => client.admin.content.discardStagedEdit.mutate(input),
    onSuccess: () => {
      announce('cms.discardStagedEdit.success');
    },
    onSettled: () => {
      invalidateAfterUnitChange(queryClient);
    },
  });
}

export type DuplicateUnitInput = Parameters<
  WebTrpcClient['admin']['content']['duplicateUnit']['mutate']
>[0];

export type DuplicateUnitResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['duplicateUnit']['mutate']>
>;

/**
 * `admin.content.duplicateUnit` (043 US2): copy a publication unit into a new
 * DRAFT in the same language, appended to its group. The web decides the
 * copy's title and address (`catalog/duplicateUnit.ts`) and the api copies
 * the unit whole in one transaction. Announced on success only; the new unit
 * appears in the list through the sweep, so the caller that wants to show it
 * reads the root id off the result.
 */
export function useDuplicateUnit(): UseMutationResult<
  DuplicateUnitResult,
  Error,
  DuplicateUnitInput
> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<DuplicateUnitResult, Error, DuplicateUnitInput>({
    mutationFn: (input) => client.admin.content.duplicateUnit.mutate(input),
    onSuccess: () => {
      announce('cms.duplicate.done');
    },
    onSettled: () => {
      invalidateAfterUnitChange(queryClient);
    },
  });
}

export type CopyUnitToLocaleInput = Parameters<
  WebTrpcClient['admin']['content']['copyUnitToLocale']['mutate']
>[0];

export type CopyUnitToLocaleResult = Awaited<
  ReturnType<WebTrpcClient['admin']['content']['copyUnitToLocale']['mutate']>
>;

/**
 * `admin.content.copyUnitToLocale` (043 US3-c): copy a publication unit into
 * ANOTHER language as a draft, appended to that language's group. The api
 * copies the addresses and payload verbatim and resolves the target category
 * itself; the web decides nothing about the copy's content.
 *
 * THE TOAST NAMES THE LANGUAGE, and it reads that from the REQUEST rather than
 * the result: it is the input the author chose, so a result shape that ever
 * stopped carrying the locale could not silently make the confirmation lie.
 * One key per target for the reason the labels have one -- Finnish inflects the
 * language name inside the phrase.
 *
 * No landing and no tab switch on success (contract B10, unlike Monista's):
 * the copy lands in ANOTHER language's drafts, which the current view does not
 * show at all, so following it would move the author's content language out
 * from under them. The toast is the whole confirmation.
 */
export function useCopyUnitToLocale(): UseMutationResult<
  CopyUnitToLocaleResult,
  Error,
  CopyUnitToLocaleInput
> {
  const client = useAdminTrpc();
  const queryClient = useQueryClient();
  const announce = useCommittedToast();
  return useMutation<CopyUnitToLocaleResult, Error, CopyUnitToLocaleInput>({
    mutationFn: (input) => client.admin.content.copyUnitToLocale.mutate(input),
    onSuccess: (_result, input) => {
      announce(
        input.targetLocale === 'sv'
          ? 'cms.copyUnitToLocale.done.sv'
          : 'cms.copyUnitToLocale.done.en',
      );
    },
    onSettled: () => {
      invalidateAfterUnitChange(queryClient);
    },
  });
}

/**
 * The cache sweep every unit lifecycle mutation runs. The list family plus
 * a PREFIX sweep over every cached detail: the unit's component ids are
 * server-side knowledge, so this layer cannot enumerate which `get` entries
 * an operation moved and must not pretend it can.
 */
function invalidateAfterUnitChange(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ADMIN_CONTENT_LIST_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ADMIN_FIXED_ENTRIES_QUERY_KEY });
  void queryClient.invalidateQueries({ queryKey: ['admin', 'content', 'get'] });
}
